import { Box, Button, Stack, Tab, Tooltip, Typography, useTheme } from '@mui/material';
import { TabContext, TabPanel } from '@mui/lab';
import { grey } from '@mui/material/colors';
import { DataGrid } from '@mui/x-data-grid';
import React, { useCallback, useEffect, useState } from 'react';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import DetachablePanel from '../../common/DetachablePanel';
import { CutterHistory } from '../../types/cutterHistory';
import * as datagrid from '../connexion/cutterHistory.DatagridColumns';
import CutterDetailsHistoryErrors, { ErrorsFilterState, initialErrorsFilterState } from './CutterDetailsHistory.errors';
import CutterDetailsHistoryCounters, { CountersFilterState, initialCountersFilterState } from './CutterDetailsHistory.counters';
import HelpIcon from '../../help/HelpIcon';
import { useAppSelector } from '../../appHooks';
import type { RootState } from '../../store';
import TabsWithExport from '../../common/TabsWithExport';
import ExportExcel from '../../common/ExportExcel';
import { useDataGridPageForSelection } from '../../styles/DataGridConfig';


export interface CutterDetailsHistorySystemProps {
    cutter: CutterHistory;
    isVersalis: boolean;
    horizontalTabValue: string;
    onHorizontalTabChange: (event: React.SyntheticEvent, newValue: string) => void;
    commonDataGridProps: any;
    horizontalTabsProps: any;
    tabPanelSx: any;
    selectedRowId?: number | null;
    selectedRowSource?: string;
}

export const CutterDetailsHistorySystem: React.FC<CutterDetailsHistorySystemProps> = ({
    cutter,
    isVersalis,
    horizontalTabValue,
    onHorizontalTabChange,
    commonDataGridProps,
    horizontalTabsProps,
    selectedRowId,
    selectedRowSource
}) => {
    const autoDetach = useAppSelector((state: RootState) => state.persistedErrorsPreferenceReducer.autoDetach);
    const [errorsDetached, setErrorsDetached] = React.useState(false);
    const [countersDetached, setCountersDetached] = React.useState(false);
    const theme = useTheme();
    const exportFill = theme.palette.mode === 'dark' ? theme.palette.success.light : theme.palette.success.dark;

    const [softwarePage, setSoftwarePage] = useDataGridPageForSelection(cutter.software, selectedRowSource !== 'softwareVersalis' ? selectedRowId : null);
    const [softwareVersalisPage, setSoftwareVersalisPage] = useDataGridPageForSelection(cutter.softwareVersalis, selectedRowSource === 'softwareVersalis' ? selectedRowId : null);
    const [hardwarePage, setHardwarePage] = useDataGridPageForSelection(cutter.hardware, selectedRowId);

    // Auto-open dialog when user has enabled the persistent preference
    useEffect(() => {
        if (autoDetach) setErrorsDetached(true);
    }, [autoDetach]);

    // Shared filter state — persists across tab ↔ dialog transitions
    const [errorsFilterState, setErrorsFilterState] = useState<ErrorsFilterState>(initialErrorsFilterState);
    const handleErrorsFilterChange = useCallback((patch: Partial<ErrorsFilterState>) => {
        setErrorsFilterState(prev => ({ ...prev, ...patch }));
    }, []);

    // Errors export data — lifted from CutterDetailsHistoryErrors via callback
    const [errorsExportData, setErrorsExportData] = useState<{ data: any[]; columns: Array<{ key: string; header: string }> } | null>(null);
    const handleErrorsExportDataChange = useCallback((data: any[], columns: Array<{ key: string; header: string }>) => {
        setErrorsExportData({ data, columns });
    }, []);

    // Counters filter state and export data
    const [countersFilterState, setCountersFilterState] = useState<CountersFilterState>(initialCountersFilterState);
    const handleCountersFilterChange = useCallback((patch: Partial<CountersFilterState>) => {
        setCountersFilterState(prev => ({ ...prev, ...patch }));
    }, []);
    const [countersExportData, setCountersExportData] = useState<{ data: any[]; columns: Array<{ key: string; header: string }> } | null>(null);
    const handleCountersExportDataChange = useCallback((data: any[], columns: Array<{ key: string; header: string }>) => {
        setCountersExportData({ data, columns });
    }, []);

    // Derive active dataset + ExcelJS columns from the visible tab
    const activeExport = React.useMemo(() => {
        const dataToExcelCols = (data: any[]): Array<{ key: string; header: string }> =>
            data.length === 0 ? [] : Object.keys(data[0]).map(k => ({ key: k, header: k }));
        switch (horizontalTabValue) {
            case 'software':
                return { data: cutter.software, columns: dataToExcelCols(cutter.software), label: `${cutter.serialNumber} software-updates` };
            case 'softwareVersalis':
                return { data: cutter.softwareVersalis, columns: dataToExcelCols(cutter.softwareVersalis), label: `${cutter.serialNumber} versalis-updates` };
            case 'hardware':
                return { data: cutter.hardware, columns: dataToExcelCols(cutter.hardware), label: `${cutter.serialNumber} hardware-changes` };
            case 'errors':
                return errorsExportData
                    ? { data: errorsExportData.data, columns: errorsExportData.columns, label: `${cutter.serialNumber} errors` }
                    : null;
            case 'counters':
                return countersExportData
                    ? { data: countersExportData.data, columns: countersExportData.columns, label: `${cutter.serialNumber} counters` }
                    : null;
            default:
                return null;
        }
    }, [horizontalTabValue, cutter, errorsExportData, countersExportData]);

    return (
        <>
        <TabContext value={horizontalTabValue}>
            <TabsWithExport
                value={horizontalTabValue}
                onChange={onHorizontalTabChange}
                {...horizontalTabsProps}
                sx={{
                    backgroundColor: (theme) => theme.palette.mode === 'light' ? grey[100] : '#252525'
                }}
                aria-label="System tabs"
                exportData={activeExport ? { ...activeExport, exportType: 'system' } : null}>
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Software ({cutter.software.length > 0 ? cutter.software.length : 'No data'})</Typography>
                        <HelpIcon helpContext="details-software-updates" placement="right" size="small" />
                    </Stack>
                } value='software' />
                <Tab sx={{ display: isVersalis ? 'block' : 'none' }} label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Software Versalis ({cutter.softwareVersalis.length > 0 ? cutter.softwareVersalis.length : 'No data'})</Typography>
                        <HelpIcon helpContext="details-versalis-updates" placement="right" size="small" />
                    </Stack>
                } value='softwareVersalis' />
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Hardware changes ({cutter.hardware.length > 0 ? cutter.hardware.length : 'No data'})</Typography>
                        <HelpIcon helpContext="details-hardware-changes" placement="right" size="small" />
                    </Stack>
                } value='hardware' />
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Counters ({cutter.reports.filter(r => r.counters).length > 0 ? cutter.reports.filter(r => r.counters).length : 'No data'})</Typography>
                        <HelpIcon helpContext="details-counters" placement="right" size="small" />
                        <Tooltip title="Detach counters in a larger view">
                            <Button
                                variant={countersDetached ? 'contained' : 'outlined'}
                                size="small"
                                color="secondary"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setCountersDetached(!countersDetached);
                                }}
                                sx={{ minWidth: 0, px: 0.5, py: 0.25, ml: 0.5, lineHeight: 1 }}
                            >
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Button>
                        </Tooltip>
                    </Stack>
                } value='counters' />
                <Tab label={
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                        <Typography variant='caption'>Errors ({cutter.errorsCount > 0 ? cutter.errorsCount : 'No data'})</Typography>
                        <HelpIcon helpContext="details-errors" placement="right" size="small" />
                        <Tooltip title="Detach errors in a larger view">
                            <Button
                                variant={errorsDetached ? 'contained' : 'outlined'}
                                size="small"
                                color="secondary"
                                onMouseDown={(e) => e.stopPropagation()}
                                onClick={(e) => {
                                    e.stopPropagation();
                                    setErrorsDetached(!errorsDetached);
                                }}
                                sx={{ minWidth: 0, px: 0.5, py: 0.25, ml: 0.5, lineHeight: 1 }}
                            >
                                <OpenInNewIcon sx={{ fontSize: 14 }} />
                            </Button>
                        </Tooltip>
                    </Stack>
                } value='errors' />
            </TabsWithExport>
            <TabPanel value='software' sx={{ p: 0 }}>
                <DataGrid
                    {...commonDataGridProps}
                    rows={cutter.software}
                    columns={(
                        isVersalis ? 
                            datagrid.softwareColumns.filter(col => col.field !== 'commercialVersion')
                            /* Add Bibliobus version, there is room enough */
                        :   datagrid.softwareColumns.filter(col => col.field !== 'bibliobusVersion')
                    )
                    }
                    {...(horizontalTabValue === 'software' && selectedRowSource !== 'softwareVersalis' && selectedRowId != null
                        ? { rowSelectionModel: { type: 'include', ids: new Set([selectedRowId]) }, onRowSelectionModelChange: () => {} }
                        : {})}
                    paginationModel={softwarePage}
                    onPaginationModelChange={setSoftwarePage}
                />
            </TabPanel>
            <TabPanel value='softwareVersalis' sx={{ p: 0 }}>
                <DataGrid
                    {...commonDataGridProps}
                    rows={cutter.softwareVersalis}
                    columns={datagrid.softwareVersalisColumns}
                    {...(horizontalTabValue === 'softwareVersalis' && selectedRowSource === 'softwareVersalis' && selectedRowId != null
                        ? { rowSelectionModel: { type: 'include', ids: new Set([selectedRowId]) }, onRowSelectionModelChange: () => {} }
                        : {})}
                    paginationModel={softwareVersalisPage}
                    onPaginationModelChange={setSoftwareVersalisPage}
                />
            </TabPanel>
            <TabPanel value='hardware' sx={{ p: 0 }}>
                <DataGrid
                    {...commonDataGridProps}
                    rows={cutter.hardware}
                    columns={datagrid.hardwareColumns}
                    {...(horizontalTabValue === 'hardware' && selectedRowId != null
                        ? { rowSelectionModel: { type: 'include', ids: new Set([selectedRowId]) }, onRowSelectionModelChange: () => {} }
                        : {})}
                    paginationModel={hardwarePage}
                    onPaginationModelChange={setHardwarePage}
                />
            </TabPanel>
            <TabPanel value='counters' sx={{ p: 0, height: '100%', width: '100%', overflow: 'hidden' }}>
                {!countersDetached && <CutterDetailsHistoryCounters
                    cutter={cutter}
                    filterState={countersFilterState}
                    onFilterChange={handleCountersFilterChange}
                    onExportDataChange={handleCountersExportDataChange}
                />}
                {countersDetached && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                        <Typography variant="body2">Counters panel is detached — click <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> in the tab to restore.</Typography>
                    </Box>
                )}
            </TabPanel>
            <TabPanel value='errors' sx={{ p: 0, height: '100%', width: '100%', overflow: 'hidden' }}>
                {!errorsDetached && <CutterDetailsHistoryErrors cutter={cutter} onDetach={() => setErrorsDetached(true)}
                    filterState={errorsFilterState} onFilterChange={handleErrorsFilterChange}
                    onExportDataChange={handleErrorsExportDataChange} />}
                {errorsDetached && (
                    <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100%', color: 'text.secondary' }}>
                        <Typography variant="body2">Errors panel is detached — click <OpenInNewIcon sx={{ fontSize: 14, verticalAlign: 'middle' }} /> in the tab to restore.</Typography>
                    </Box>
                )}
            </TabPanel>
        </TabContext>

        <DetachablePanel
            open={countersDetached}
            onClose={() => setCountersDetached(false)}
            panelId="counters"
            title={<>Counters — <b>{cutter.serialNumber}</b></>}
            subtitle="Select counters and use the file filter to narrow results."
            headerActions={countersExportData && (
                <Tooltip title={`Export counters to Excel`} placement="left">
                    <span>
                        <ExportExcel
                            label={`${cutter.serialNumber} counters`}
                            results={countersExportData.data}
                            total={countersExportData.data.length}
                            exportType="details"
                            columns={countersExportData.columns}
                            size="small"
                            sx={{ width: '28px', height: '28px', fill: exportFill }}
                        />
                    </span>
                </Tooltip>
            )}
        >
            <CutterDetailsHistoryCounters cutter={cutter}
                isDetached
                filterState={countersFilterState} onFilterChange={handleCountersFilterChange}
                onExportDataChange={handleCountersExportDataChange} />
        </DetachablePanel>

        <DetachablePanel
            open={errorsDetached}
            onClose={() => setErrorsDetached(false)}
            panelId="errors"
            title={<>Errors — <b>{cutter.serialNumber}</b></>}
            subtitle="Use calendar icon to select specific day."
            headerActions={errorsExportData && (
                <Tooltip title={`Export errors to Excel`} placement="left">
                    <span>
                        <ExportExcel
                            label={`${cutter.serialNumber} errors`}
                            results={errorsExportData.data}
                            total={errorsExportData.data.length}
                            exportType="details"
                            columns={errorsExportData.columns}
                            size="small"
                            sx={{ width: '28px', height: '28px', fill: exportFill }}
                        />
                    </span>
                </Tooltip>
            )}
        >
            <CutterDetailsHistoryErrors cutter={cutter}
                isDetached
                filterState={errorsFilterState} onFilterChange={handleErrorsFilterChange}
                onExportDataChange={handleErrorsExportDataChange} />
        </DetachablePanel>
        </>
    );
};

export default CutterDetailsHistorySystem;